import Link from 'next/link';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';

import {
  ToastContainer,
  ToastContent,
  ToastTitle,
  useAutoDismiss,
} from '@/components/TransactionToast/ToastComponents';

import { ADVANCED_ORDER_TYPE_LABELS, type AdvancedOrderType } from '@/constants/advancedOrder';
import { NETWORK_NAME_PER_CHAIN_ID } from '@/constants/chain';

import { fontStyles } from '@/styles/typography';

import { ChainAvatar } from '@/ui/Avatars/ChainAvatar';
import { ClockIcon } from '@/ui/Icons/ClockIcon';

import { routes } from '@/utils/routes';

import { FeedbackAnimation } from '../animations/FeedbackAnimation';

export type LimitOrderToastStatus =
  | 'submitted'
  | 'cancelPending'
  | 'userCancelled'
  | 'success'
  | 'error'
  | 'submissionFailed';

type LimitOrderToastToken = {
  chainId: number;
  symbol: string;
};

type LimitOrderToastProps = {
  status: LimitOrderToastStatus;
  sellAmount: string;
  buyAmount: string;
  sellToken: LimitOrderToastToken;
  buyToken: LimitOrderToastToken;
  orderType?: AdvancedOrderType;
  bulkOrderCount?: number;
  toastId: string | number;
};

type ShowLimitOrderToastParams = Omit<LimitOrderToastProps, 'toastId'> & {
  toastId?: string | number;
};

function getLimitOrderToastTitle(
  status: LimitOrderToastStatus,
  orderType: AdvancedOrderType,
): string {
  const label = ADVANCED_ORDER_TYPE_LABELS[orderType];
  switch (status) {
    case 'submitted':
      return `${label} order placed!`;
    case 'cancelPending':
      return `Canceling ${label} order...`;
    case 'userCancelled':
      return 'Transaction canceled';
    case 'success':
      return `${label} order canceled!`;
    case 'error':
      return `Couldn't cancel ${label} order`;
    case 'submissionFailed':
      return `Couldn't place ${label} order`;
    default: {
      const exhaustiveStatus: never = status;
      throw new Error(`Unknown toast status: ${exhaustiveStatus}`);
    }
  }
}

export const showLimitOrderToast = ({ toastId: toastIdProp, ...data }: ShowLimitOrderToastParams) =>
  toast.custom((toastId) => <LimitOrderToast {...data} toastId={toastId} />, {
    duration: Infinity,
    unstyled: true,
    ...(toastIdProp !== undefined && { id: toastIdProp }),
  });

export const LimitOrderToast = ({
  status,
  sellAmount,
  buyAmount,
  sellToken,
  buyToken,
  orderType = 'limit',
  bulkOrderCount,
  toastId,
}: LimitOrderToastProps) => {
  const [isHovered, setIsHovered] = useState(false);

  const isPending = status === 'cancelPending';
  const isError = ['userCancelled', 'error', 'submissionFailed'].includes(status);
  const isBulkCancellation = bulkOrderCount !== undefined;

  const feedback = isPending ? undefined : isError ? 'error' : 'success';
  const title =
    status === 'success' && isBulkCancellation
      ? 'All limit orders canceled!'
      : getLimitOrderToastTitle(status, orderType);

  const onDismiss = useCallback(() => toast.dismiss(toastId), [toastId]);

  const handleHeightChange = useCallback(() => {
    const params = {
      toastId,
      status,
      sellAmount,
      buyAmount,
      sellToken,
      buyToken,
      orderType,
      bulkOrderCount,
    };
    showLimitOrderToast(params);
  }, [bulkOrderCount, buyAmount, orderType, sellAmount, sellToken, buyToken, status, toastId]);

  useAutoDismiss(isHovered, onDismiss, isPending);

  const orderDescription = isBulkCancellation
    ? bulkOrderCount
      ? `${bulkOrderCount} limit order${bulkOrderCount === 1 ? '' : 's'}`
      : 'Limit orders'
    : `${sellAmount} ${sellToken.symbol} to ${buyAmount} ${buyToken.symbol}`;

  return (
    <ToastContainer
      onClose={onDismiss}
      onHeightChange={handleHeightChange}
      showProgressAnimation={isPending}
      onHoverChange={setIsHovered}
    >
      <FeedbackAnimation feedback={feedback} className="relative z-10 shrink-0">
        <div className="m-1.5 flex size-7 items-center justify-center rounded-full bg-token-gray-400 text-token-gray-800">
          <ClockIcon className="size-4" aria-hidden="true" />
        </div>
      </FeedbackAnimation>
      <div className="relative z-10 flex min-w-0 flex-1 flex-col gap-1">
        <ToastTitle>{title}</ToastTitle>
        <ToastContent>
          <div className="flex items-center whitespace-nowrap">
            <span
              className={fontStyles({
                variant: 'LABEL_MD',
                class: 'text-token-gray-1600 min-w-0 block truncate',
              })}
            >
              {orderDescription}
            </span>
            {!isBulkCancellation && (
              <>
                <span className={fontStyles({ variant: 'LABEL_MD', class: 'text-token-gray-700' })}>
                  &nbsp;on&nbsp;
                </span>
                <div className="flex shrink-0 items-center gap-1">
                  <ChainAvatar
                    chainId={sellToken.chainId}
                    alt={NETWORK_NAME_PER_CHAIN_ID[sellToken.chainId]}
                    size={16}
                  />
                  <span
                    className={fontStyles({
                      variant: 'LABEL_MD',
                      class: 'text-token-gray-700 whitespace-nowrap',
                    })}
                  >
                    {NETWORK_NAME_PER_CHAIN_ID[sellToken.chainId]}
                  </span>
                </div>
              </>
            )}
          </div>
        </ToastContent>
        {status === 'submitted' && (
          <Link
            className={fontStyles({
              variant: 'LABEL_MD',
              class: [
                'text-token-purple-900 cursor-pointer p-1.5 -ml-1 rounded-[8px] hover:bg-token-purple-200',
                'active:bg-token-purple-200 focus:outline-none focus-visible:ring-2 self-start',
                'focus-visible:ring-token-purple-500 transition-all duration-150 ease-in-out',
              ],
            })}
            onClick={onDismiss}
            href={{ pathname: routes.TRADE_HISTORY, query: { tab: 'open-orders' } }}
          >
            View open order
          </Link>
        )}
      </div>
    </ToastContainer>
  );
};
